<script lang="ts">
  import { onDestroy, untrack } from 'svelte';
  import { flowSeries, leakInterval } from '../../utils/flowHistory';
  import { Chart, registerables } from 'chart.js';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import annotationPlugin from 'chartjs-plugin-annotation';
  import 'chartjs-adapter-date-fns';
  import { formatFlowAlarm } from '../../utils/alarms';

  Chart.register(...registerables, zoomPlugin, annotationPlugin);

  function getYAtX(x: number, datasetMeta: any, stepped: any, yBottom: number): number {
    const points = datasetMeta.data;
    if (!points || points.length === 0) return yBottom;
    
    // Filter out points that are null/NaN/skipped
    const validPoints = points.filter((p: any) => p && !isNaN(p.x) && !isNaN(p.y));
    if (validPoints.length === 0) return yBottom;
    
    if (x <= validPoints[0].x) return validPoints[0].y;
    if (x >= validPoints[validPoints.length - 1].x) return validPoints[validPoints.length - 1].y;
    
    for (let i = 0; i < validPoints.length - 1; i++) {
      const p1 = validPoints[i];
      const p2 = validPoints[i + 1];
      if (x >= p1.x && x <= p2.x) {
        if (stepped === 'before') {
          return p1.y;
        } else if (stepped) {
          return p1.y;
        }
        const pct = (x - p1.x) / (p2.x - p1.x);
        return p1.y + pct * (p2.y - p1.y);
      }
    }
    return validPoints[validPoints.length - 1].y;
  }

  let { data = [], anomalies = [], label = "Flow Rate", color = "#3b82f6", range = '24h', unitPreference = 'metric', customVolumeUnit = null, timeWindow = null, metric = false, sensorSeries = [], valveReports = [], controls = true, sharedViewport = null, sharedHover = null, onViewport = undefined, onHoverTime = undefined } = $props();

  const lastKnown = $derived.by(() => {
    const rows = data.filter((row: any) => typeof row.total_flow === 'number' && Number.isFinite(row.total_flow) && Number.isFinite(Date.parse(row.timestamp ?? row.ingested_at)));
    return [...rows].sort((a: any, b: any) => Date.parse(a.timestamp ?? a.ingested_at) - Date.parse(b.timestamp ?? b.ingested_at)).at(-1);
  });

  const totalFlowColor = '#8b5cf6'; // violet for total flow line

  const LPH_TO_GPM = 1 / (3.785411784 * 60); // L/h to gallons per minute

  let canvasEl: HTMLCanvasElement;
  let chart: Chart | null = null;
  let isZoomed = $state(false);
  let liveTimeStr = $state('');

  // ── Dark mode detection ──
  function isDark(): boolean {
    return document.documentElement.classList.contains('dark');
  }

  // ── Theme-aware colors ──
  function themeColors() {
    const dark = isDark();
    return {
      grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      tick: dark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)',
      tooltip: dark ? 'rgba(20,24,32,0.62)' : 'rgba(15,15,15,0.62)',
    };
  }

  function formatTooltipTitle(isoStr: string): string {
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return isoStr;
    return dt.toLocaleString([], {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  // Check if data has consumption values (aggregated) or total_flow (raw)
  function hasTotalFlow(chartData: any[]): boolean {
    if (!chartData || chartData.length === 0) return false;
    return chartData.some((d: any) => d.total_flow != null || d.hourly_consumption != null || d.daily_consumption != null);
  }

  function volumeUnit(): string { return customVolumeUnit || (unitPreference === 'imperial' ? 'gal' : 'L'); }
  function convertFlowRate(lph: number): number {
    return unitPreference === 'imperial' ? lph * LPH_TO_GPM : lph;
  }

  function valveMarkerColor(event: any): string {
    const label = String(event?.label || '').toLowerCase();
    if (label === 'closed') return '#ef4444';
    if (label === 'open' || label.startsWith('open (')) return '#22c55e';
    return '#f59e0b';
  }

  function buildChart() {
    if (chart) chart.destroy();
    if (!canvasEl) return;

    const chartData = data;
    const tc = themeColors();
    const dark = isDark();
    let series = flowSeries(chartData, !metric && !customVolumeUnit && unitPreference === 'imperial', timeWindow ? timeWindow.bucketWidth : range === 'Live' ? 0 : range === '30d' ? 86400_000 : 3600_000, timeWindow?.end ?? Date.now(), true);
    const showTF = hasTotalFlow(chartData);
    const aggregate = chartData.some((d: any) => d.hourly_consumption != null || d.daily_consumption != null) && !chartData.some((d: any) => d.total_flow != null);

    // If raw telemetry has gaps > 2 minutes (or > 6 hours for pulse readers) between consecutive reports, insert a break point so solid line doesn't span across the gap
    if (!aggregate && !metric && series.length > 1) {
      const gapThreshold = customVolumeUnit ? 6 * 3600_000 : 120_000;
      const augmentedSeries: typeof series = [];
      for (let i = 0; i < series.length; i++) {
        augmentedSeries.push(series[i]);
        if (i < series.length - 1) {
          const timeDiff = series[i + 1].time - series[i].time;
          if (timeDiff > gapThreshold) {
            augmentedSeries.push({
              time: series[i].time + 1,
              rate: null,
              total: null,
              startTotal: null,
              endTotal: null
            });
          }
        }
      }
      series = augmentedSeries;
    }

    const rawTimestamps = series.map(p => new Date(p.time).toISOString());
    const labels = rawTimestamps;
    const flowPoints = series.map(p => ({ x: p.time, y: p.rate }));
    const totalFlowPoints = series.map(p => ({ x: p.time, y: p.total }));
    const bucketWidth = timeWindow?.bucketWidth || (range === '30d' ? 86400_000 : 3600_000);
    const volumeLabel = customVolumeUnit ? 'Dial reading' : 'Meter total';

    // Gradient fill for flow line
    const ctx = canvasEl.getContext('2d')!;
    const h = canvasEl.parentElement?.clientHeight || 300;
    const gradient = ctx.createLinearGradient(0, 0, 0, h);
    gradient.addColorStop(0, color + (dark ? '30' : '20'));
    gradient.addColorStop(1, color + '00');

    let currentMouseX: number | null = null;
    let currentMouseY: number | null = null;
    let currentMouseTime: number | null = null;

    const datasets: any[] = [{
      label,
      data: flowPoints,
      parsing: false,
      borderColor: color,
      backgroundColor: gradient,
      borderWidth: 1.5,
      fill: true,
      tension: 0,
      stepped: metric ? false : 'before',
      pointRadius: metric ? 2 : (customVolumeUnit && !aggregate ? 3 : 0),
      pointHoverRadius: 5,
      pointHitRadius: 25,
      pointHoverBackgroundColor: color,
      pointHoverBorderColor: dark ? '#1a1a2e' : '#ffffff',
      pointHoverBorderWidth: 2,
      yAxisID: 'y',
      order: 1, // draw on top
    }];

    if (showTF) {
      const tfGradient = ctx.createLinearGradient(0, 0, 0, h);
      tfGradient.addColorStop(0, totalFlowColor + (dark ? '20' : '12'));
      tfGradient.addColorStop(1, totalFlowColor + '00');

      datasets.push({
        label: `${volumeLabel} (${volumeUnit()})`,
        data: totalFlowPoints,
        parsing: false,
        borderColor: totalFlowColor + 'aa',
        backgroundColor: tfGradient,
        borderWidth: 1.5,
        fill: true,
        tension: 0,
        stepped: false,
        pointRadius: customVolumeUnit && !aggregate ? 2.5 : 0,
        pointHoverRadius: 4,
        pointHitRadius: 25,
        pointHoverBackgroundColor: totalFlowColor,
        pointHoverBorderColor: dark ? '#1a1a2e' : '#ffffff',
        pointHoverBorderWidth: 2,
        yAxisID: 'y1',
        order: 2,
      } as any);
    }
    
    if (showTF) {
      const end = timeWindow?.end ?? (isZoomed && sharedViewport?.max ? sharedViewport.max : Date.now());
      const gapSegments: { x: number, y: number }[][] = [];

      // Detect internal missing intervals between buckets or reports in the past
      let inGap = false;
      let prevValid: any = null;
      for (let i = 0; i < series.length; i++) {
        const pt = series[i];
        if (pt.total != null && Number.isFinite(pt.total)) {
          if (inGap && prevValid) {
            if (pt.time > prevValid.time + 1000) {
              gapSegments.push([
                { x: prevValid.time, y: prevValid.total },
                { x: pt.time, y: prevValid.total }
              ]);
            }
            inGap = false;
          }
          prevValid = pt;
        } else {
          if (prevValid) {
            inGap = true;
          }
        }
      }

      // Detect trailing missing interval up to the end of the time window
      let lastTotalTime: number | null = null;
      let lastTotalVal: number | null = null;
      if (!aggregate && lastKnown) {
        lastTotalTime = Date.parse(lastKnown.timestamp ?? lastKnown.ingested_at);
        lastTotalVal = lastKnown.total_flow / (!customVolumeUnit && unitPreference === 'imperial' ? 3.785411784 : 1);
      } else {
        const latestPt = series.filter(p => typeof p.total === 'number' && Number.isFinite(p.total)).at(-1);
        if (latestPt) {
          lastTotalTime = latestPt.time;
          lastTotalVal = latestPt.total;
        }
      }

      if (lastTotalTime != null && lastTotalVal != null && end > lastTotalTime + 60_000) {
        gapSegments.push([
          { x: lastTotalTime, y: lastTotalVal },
          { x: end, y: lastTotalVal }
        ]);
      }

      gapSegments.forEach((segment) => {
        datasets.push({
          label: segment[1].x === end ? 'Last known meter total' : 'Missing telemetry (assumed unchanged)',
          data: segment,
          parsing: false,
          borderColor: totalFlowColor + '66',
          borderDash: [3, 6],
          borderWidth: 1.5,
          pointRadius: 0,
          pointHitRadius: 8,
          fill: false,
          yAxisID: 'y1',
          order: 3,
          reportTime: segment[0].x,
          liveContinuation: segment[1].x === end,
        });
      });
    }

    if (sensorSeries.length) {
      datasets.length = 0;
      const end = timeWindow?.end ?? (isZoomed && sharedViewport?.max ? sharedViewport.max : Date.now());
      for (const sensor of sensorSeries) {
        // Filter out null/undefined points so isolated nulls from other report types don't break lines
        const validPoints = (sensor.points || [])
          .filter((point: any) => point.value != null && Number.isFinite(point.value) && Date.parse(point.timestamp) <= end)
          .map((point: any) => ({ x: Date.parse(point.timestamp), y: point.value }));
        
        // Connect consecutive readings; only break line if reporting gap > 6 hours
        const pointsWithBreaks: { x: number, y: number | null }[] = [];
        for (let i = 0; i < validPoints.length; i++) {
          pointsWithBreaks.push(validPoints[i]);
          if (i < validPoints.length - 1) {
            const gap = validPoints[i + 1].x - validPoints[i].x;
            if (gap > 6 * 3600 * 1000) {
              pointsWithBreaks.push({ x: validPoints[i].x + 1, y: null });
            }
          }
        }

        datasets.push({
          label: sensor.label,
          data: pointsWithBreaks,
          parsing: false,
          yAxisID: sensor.axis,
          borderColor: sensor.color,
          backgroundColor: sensor.color,
          pointRadius: 2.5,
          pointHoverRadius: 5,
          borderWidth: 1.5,
          fill: false,
          tension: 0.1,
          spanGaps: false,
          sensor: true
        });
        const latest = validPoints.at(-1);
        if (latest && latest.x < end) {
          datasets.push({
            label: `Last known ${sensor.label}`,
            continuationLabel: sensor.label,
            data: [latest, { x: end, y: latest.y }],
            parsing: false,
            yAxisID: sensor.axis,
            borderColor: sensor.color + '66',
            borderDash: [3, 6],
            borderWidth: 1,
            pointRadius: 0,
            pointHitRadius: 8,
            fill: false,
            reportTime: latest.x,
            liveContinuation: true
          });
        }
      }
    }
    if (valveReports.length) datasets.push({label: 'Reported valve position', valve: true,
      data: valveReports.map((event: any) => ({x:event.time, y:0.96, event})), parsing: false,
      yAxisID:'valveAxis', showLine:false, pointStyle:'triangle', pointRadius:5, pointHoverRadius:7,
      pointHitRadius: 20,
      backgroundColor: (context: any) => valveMarkerColor(context.raw?.event),
      borderColor: (context: any) => valveMarkerColor(context.raw?.event), order:0});

    const hardwareLeaks = (anomalies || []).filter((a: any) => a.log_type === 'hardware' && a.flow_alarm > 0);
    if (hardwareLeaks.length) {
      const leakPoints: any[] = [];
      for (const a of hardwareLeaks) {
        const interval = leakInterval(a);
        if (!interval) continue;
        const startT = interval.detected;
        const endT = interval.end;
        // Sample points across the active leak window (at least every 10 seconds)
        // so hovering anywhere inside the leak interval triggers the tooltip even when zoomed in tight
        const step = Math.max(5000, Math.min(15000, (endT - startT) / 6));
        for (let t = startT; t <= endT; t += step) {
          leakPoints.push({ x: t, y: 0, anomaly: a });
        }
        if (leakPoints.length === 0 || leakPoints[leakPoints.length - 1].x < endT) {
          leakPoints.push({ x: endT, y: 0, anomaly: a });
        }
      }

      datasets.push({
        label: 'Hardware Leak Event',
        leakMarker: true,
        data: leakPoints,
        parsing: false,
        yAxisID: 'y',
        showLine: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 40,
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
        order: 0
      });
    }

    const leakSpanPlugin = {
      id: 'leakSpan',
      beforeDatasetsDraw(internalChart: Chart) {
        if (metric) return;
        const ctx = internalChart.ctx;
        const xAxis = internalChart.scales['x'];
        // Shade beneath the measured volume curve, behind both datasets.
        const volumeIndex = internalChart.data.datasets.findIndex(ds => ds.yAxisID === 'y1');
        const index = volumeIndex >= 0 ? volumeIndex : 0;
        const yAxis = internalChart.scales[volumeIndex >= 0 ? 'y1' : 'y'];
        const datasetMeta = internalChart.getDatasetMeta(index);
        const stepped = internalChart.data.datasets[index].stepped;
        if (!anomalies?.length || !datasetMeta.data.length) return;

        ctx.save();
        ctx.beginPath();
        ctx.rect(xAxis.left, yAxis.top, xAxis.right - xAxis.left, yAxis.bottom - yAxis.top);
        ctx.clip();

        function shade(from: number, to: number, rgb: string) {
          const left = Math.max(xAxis.getPixelForValue(from), xAxis.left);
          const right = Math.min(xAxis.getPixelForValue(to), xAxis.right);
          if (right <= left) return;
          ctx.beginPath();
          ctx.moveTo(left, yAxis.bottom);
          let lastY = getYAtX(left, datasetMeta, stepped, yAxis.bottom);
          ctx.lineTo(left, lastY);
          for (const point of datasetMeta.data) {
            if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
            if (point.x > left && point.x < right) {
              if (stepped) ctx.lineTo(point.x, lastY);
              ctx.lineTo(point.x, point.y);
              lastY = point.y;
            }
          }
          if (stepped) ctx.lineTo(right, lastY);
          ctx.lineTo(right, getYAtX(right, datasetMeta, stepped, yAxis.bottom));
          ctx.lineTo(right, yAxis.bottom);
          ctx.closePath();
          const gradient = ctx.createLinearGradient(0, yAxis.top, 0, yAxis.bottom);
          gradient.addColorStop(0, `rgba(${rgb}, 0.30)`);
          gradient.addColorStop(1, `rgba(${rgb}, 0.06)`);
          ctx.fillStyle = gradient;
          ctx.fill();
        }

        for (const alarm of anomalies) {
          if (alarm.log_type !== 'hardware' || !(alarm.flow_alarm > 0)) continue;
          const interval = leakInterval(alarm);
          if (!interval) continue;
          // Unknown onset is never backfilled. Cleared events stop at clearance.
          if (interval.hasStart) shade(interval.start, interval.detected, '59, 130, 246');
          shade(interval.detected, interval.end, '239, 68, 68');
        }
        ctx.restore();
      },

      afterDatasetsDraw(internalChart: Chart) {
        if (metric) {
          const ctx = internalChart.ctx, x = internalChart.scales.x, y = internalChart.scales.y;
          ctx.save();
          for (const alarm of anomalies) {
            if (alarm.log_type !== 'hardware' || !(alarm.flow_alarm > 0)) continue;
            const interval = leakInterval(alarm);
            if (!interval) continue;
            for (const [time, color] of [[interval.hasStart ? interval.start : NaN, '#3b82f6'], [interval.detected, '#ef4444'], [interval.hasClear ? interval.end : NaN, '#22c55e']] as const) {
              if (!Number.isFinite(time) || time < x.min || time > x.max) continue;
              const pixel = x.getPixelForValue(time);
              ctx.beginPath(); ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.setLineDash([2,3]);
              ctx.moveTo(pixel, y.top); ctx.lineTo(pixel, y.bottom); ctx.stroke();
            }
          }
          ctx.restore();
          return;
        }
        const ctx = internalChart.ctx;
        const xAxis = internalChart.scales['x'];
        const volumeIndex = internalChart.data.datasets.findIndex(ds => ds.yAxisID === 'y1');
        const index = volumeIndex >= 0 ? volumeIndex : 0;
        const yAxis = internalChart.scales[volumeIndex >= 0 ? 'y1' : 'y'];
        const datasetMeta = internalChart.getDatasetMeta(index);
        const stepped = internalChart.data.datasets[index].stepped;

        if (!anomalies || anomalies.length === 0) return;
        
        ctx.save();
        
        anomalies.forEach((a) => {
          // Only show hardware leaks (disregard ML anomalies)
          const isHardwareLeak = a.log_type === 'hardware' && a.flow_alarm > 0;
          if (!isHardwareLeak) return;

          const interval = leakInterval(a);
          if (!interval) return;
          const { detected: dTime, start: sTime, end: eTime, active: isActive } = interval;
          
          // Check if within chart bounds
          if (eTime < xAxis.min || sTime > xAxis.max) return;
          
          const startX = xAxis.getPixelForValue(sTime);
          const detectX = xAxis.getPixelForValue(dTime);
          const endX = xAxis.getPixelForValue(eTime);
          const detailedMarkers = Math.abs(endX - startX) >= 80;
          
          // 1. Flow Start Marker
          if (interval.hasStart && Math.abs(detectX - startX) > 10 && startX >= xAxis.left && startX <= xAxis.right) {
            const sY = getYAtX(startX, datasetMeta, stepped, yAxis.bottom);
            
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(startX, sY);
            ctx.lineTo(startX, yAxis.bottom);
            ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.setLineDash([]);
            
            ctx.beginPath();
            ctx.arc(startX, sY, 3, 0, 2 * Math.PI);
            ctx.fillStyle = '#3b82f6';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.fill();
            ctx.stroke();
            
            ctx.fillStyle = 'rgba(59, 130, 246, 0.95)';
            ctx.font = '500 9px Inter';
            ctx.textAlign = 'center';
            if (detailedMarkers) ctx.fillText('Flow Start', startX, yAxis.bottom - 15);
          }
          
          // 2. Leak Detected Marker
          const shouldDrawDetect = true;
          if (shouldDrawDetect && detectX >= xAxis.left && detectX <= xAxis.right) {
            const dY = getYAtX(detectX, datasetMeta, stepped, yAxis.bottom);
            
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(detectX, dY);
            ctx.lineTo(detectX, yAxis.bottom);
            ctx.strokeStyle = a.severity === 'critical' ? 'rgba(239, 68, 68, 0.7)' : 'rgba(245, 158, 11, 0.7)';
            ctx.lineWidth = 1.2;
            ctx.stroke();
            ctx.setLineDash([]);
            
            ctx.beginPath();
            ctx.arc(detectX, dY, 3, 0, 2 * Math.PI);
            ctx.fillStyle = a.severity === 'critical' ? '#ef4444' : '#f59e0b';
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1.5;
            ctx.fill();
            ctx.stroke();
            
            ctx.fillStyle = a.severity === 'critical' ? '#ef4444' : '#f59e0b';
            ctx.font = 'bold 9px Inter';
            ctx.textAlign = 'center';
            
            let markerText = 'Leak Detected ⚠️';
            
            if (a.anomaly_type === 'Hardware Malfunction' || (a.malfunction_alarms && a.malfunction_alarms.length > 0)) {
              if (a.malfunction_alarms && a.malfunction_alarms.length === 1 && a.malfunction_alarms[0] === 'bl') {
                if (detailedMarkers) ctx.fillText('Battery Low ⚠️', detectX, yAxis.bottom - 28);
              } else {
                if (detailedMarkers) ctx.fillText('Hardware Fault ⚠️', detectX, yAxis.bottom - 28);
              }
            } else if (a.auto_shutoff) {
              if (detailedMarkers) ctx.fillText('Auto Shutoff 🛑', detectX, yAxis.bottom - 32);
              ctx.fillStyle = '#ef4444';
              if (detailedMarkers) ctx.fillText(markerText.replace(' ⚠️', ''), detectX, yAxis.bottom - 44);
            } else {
              if (detailedMarkers) ctx.fillText(markerText, detectX, yAxis.bottom - 28);
            }
          }
          
          // 3. Auto Shutoff Triggered line
          if (shouldDrawDetect && a.auto_shutoff && detectX >= xAxis.left && detectX <= xAxis.right) {
            ctx.beginPath();
            ctx.moveTo(detectX, yAxis.top);
            ctx.lineTo(detectX, yAxis.bottom);
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
          
          // 4. Leak Cleared Marker
          if (interval.hasClear && Math.abs(endX - detectX) > 10) {
            const clearTime = new Date(a.cleared_at).getTime();
            const clearX = xAxis.getPixelForValue(clearTime);
            if (clearX >= xAxis.left && clearX <= xAxis.right) {
              const cY = getYAtX(clearX, datasetMeta, stepped, yAxis.bottom);
              
              ctx.beginPath();
              ctx.setLineDash([4, 4]);
              ctx.moveTo(clearX, cY);
              ctx.lineTo(clearX, yAxis.bottom);
              ctx.strokeStyle = 'rgba(16, 185, 129, 0.6)'; // emerald green
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.setLineDash([]);
              
              ctx.beginPath();
              ctx.arc(clearX, cY, 3, 0, 2 * Math.PI);
              ctx.fillStyle = '#10b981'; // emerald green
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1.5;
              ctx.fill();
              ctx.stroke();
              
              ctx.fillStyle = '#10b981';
              ctx.font = '500 9px Inter';
              ctx.textAlign = 'center';
              if (detailedMarkers) ctx.fillText('Cleared ✅', clearX, yAxis.bottom - 15);
            }
          }
        });
        
        ctx.restore();
      }
    };

    chart = new Chart(canvasEl, {
      type: 'line',
      data: { labels, datasets },
      plugins: [leakSpanPlugin, {
        id: 'sharedTimeCursor',
        afterDraw(current: Chart) {
          if (sharedHover == null) return;
          const x = current.scales.x, y = current.scales.y;
          if (sharedHover < x.min || sharedHover > x.max) return;
          const pixel = x.getPixelForValue(sharedHover), ctx = current.ctx;
          ctx.save(); ctx.beginPath(); ctx.strokeStyle = tc.tick; ctx.lineWidth = 1; ctx.setLineDash([3,3]);
          ctx.moveTo(pixel, y.top); ctx.lineTo(pixel, y.bottom); ctx.stroke(); ctx.restore();
        }
      }],
      options: {
        responsive: true,
        onHover: (event: any, _elements: any, current: Chart) => {
          if (event.x != null) {
            currentMouseX = event.x;
            currentMouseY = event.y;
            currentMouseTime = current.scales.x.getValueForPixel(event.x);
          } else {
            currentMouseX = null;
            currentMouseY = null;
            currentMouseTime = null;
          }
          onHoverTime?.(currentMouseTime);
        },
        maintainAspectRatio: false,
        layout: { 
          padding: { 
            top: 32, // Headroom so zoom controls and peak data never collide or get clipped
            bottom: lastKnown ? 18 : 4 
          } 
        },
        // Unit changes rebuild the chart and restore x bounds synchronously.
        // An initial animation can otherwise keep overwriting the restored geometry.
        animation: false,
        interaction: {
          intersect: false,
          mode: 'index',
          axis: 'x',
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            intersect: false,
            mode: 'index',
            axis: 'x',
            backgroundColor: tc.tooltip,
            titleFont: { size: 11, family: 'Inter', weight: 500 as any },
            bodyFont: { size: 12, family: 'Inter', weight: 400 as any },
            titleColor: 'rgba(255,255,255,0.92)',
            bodyColor: 'rgba(255,255,255,0.82)',
            padding: { top: 8, bottom: 8, left: 12, right: 12 },
            cornerRadius: 8,
            displayColors: true,
            boxWidth: 8,
            boxHeight: 8,
            boxPadding: 4,
            filter: (item: any) => {
              // 0. Leak marker points: only allow if cursor is within that leak's detected-to-cleared window
              if (item.dataset.leakMarker) {
                const a = item.raw?.anomaly;
                if (!a) return false;
                const interval = leakInterval(a);
                if (!interval) return false;
                const cursorTime = currentMouseTime ?? item.chart.scales.x.getValueForPixel(item.element?.x);
                if (cursorTime == null) return false;
                return cursorTime >= (interval.detected - 4000) && cursorTime <= (interval.end + 4000);
              }

              // 1. Valve report markers: only show if the mouse cursor is horizontally close to the triangle (within 24px)
              if (item.dataset.valve) {
                const meta = item.chart.getDatasetMeta(item.datasetIndex);
                const point = meta?.data?.[item.dataIndex];
                if (point && currentMouseX != null) {
                  return Math.abs(currentMouseX - point.x) <= 24;
                }
                return true;
              }

              // 2. Gap continuation segments: only show if the cursor is within that specific gap's time window
              if (item.dataset.reportTime != null) {
                const segment = item.dataset.data;
                if (!segment || segment.length < 2) return false;
                const start = segment[0].x;
                const end = segment[1].x;
                const targetTime = currentMouseTime ?? item.chart.scales.x.getValueForPixel(item.element?.x);
                return targetTime != null && targetTime >= start && targetTime <= end;
              }

              return true;
            },
            callbacks: {
              title: (items: any[]) => {
                const valveItem = items.find((it: any) => it.dataset.valve);
                if (valveItem) return formatTooltipTitle(new Date(valveItem.raw.x).toISOString());
                const leakItem = items.find((it: any) => it.dataset.leakMarker);
                if (leakItem) {
                  const targetTime = currentMouseTime ?? leakItem.raw.x;
                  return formatTooltipTitle(new Date(targetTime).toISOString());
                }
                if (items[0]?.dataset.sensor) return formatTooltipTitle(new Date(items[0].raw.x).toISOString());
                if (items[0]?.dataset.continuationLabel) return 'Last known sensor values';
                if (items[0]?.dataset.reportTime != null) return `Last reported ${formatTooltipTitle(new Date(items[0].dataset.reportTime).toISOString())}`;
                if (items.length > 0) {
                  const timestamp = rawTimestamps[items[0].dataIndex];
                  if (!timestamp && items[0].raw?.x) return formatTooltipTitle(new Date(items[0].raw.x).toISOString());
                  if (aggregate && timestamp) {
                    const start = Math.floor(Date.parse(timestamp) / bucketWidth) * bucketWidth;
                    return `${formatTooltipTitle(new Date(start).toISOString())} – ${formatTooltipTitle(new Date(Math.min(start + bucketWidth, Date.now())).toISOString())}`;
                  }
                  return formatTooltipTitle(timestamp || new Date().toISOString());
                }
                return '';
              },
              label: (ctx: any) => {
                if (ctx.dataset.leakMarker) return null;
                if (ctx.dataset.valve) return ` Valve reported: ${ctx.raw.event.label}${ctx.raw.event.initial ? ' (first observed)' : ''}`;
                const val = ctx.parsed.y;
                if (ctx.dataset.continuationLabel) return ` Last known ${ctx.dataset.continuationLabel}: ${val.toFixed(2)} · reported ${formatTooltipTitle(new Date(ctx.dataset.reportTime).toISOString())} (unconfirmed since report)`;
                if (ctx.dataset.sensor) return ` ${ctx.dataset.label}: ${val == null ? '—' : val.toFixed(2)}`;
                if (ctx.dataset.reportTime != null) return ` Last known meter total: ${val.toFixed(2)} ${volumeUnit()} (unconfirmed since report)`;
                if (ctx.datasetIndex === 0) {
                  return ` ${label}: ${val != null ? val.toFixed(2) : '—'}`;
                } else if (ctx.datasetIndex === 1) {
                  return ` ${volumeLabel}: ${val != null ? val.toFixed(2) : '—'} ${volumeUnit()}`;
                }
                return null;
              },
              afterBody: (items: any[]) => {
                if (items.length === 0 || !anomalies?.length) return '';
                
                const chartInstance = items[0].chart;
                const xAxis = chartInstance.scales.x;
                const xPos = currentMouseX ?? chartInstance.tooltip?.caretX;
                if (xPos == null || !xAxis) return '';

                // Pick the candidate hardware leak strictly within the area after leak detected and before leak cleared (if cleared)
                let activeAnomaly: any = null;
                let minDistancePx = Infinity;

                for (const a of anomalies) {
                  const isHardwareLeak = a.log_type === 'hardware' && a.flow_alarm > 0;
                  if (!isHardwareLeak) continue;

                  const interval = leakInterval(a);
                  if (!interval) continue;

                  // Viewport culling: leak must overlap current visible x-axis range
                  if (interval.end < xAxis.min || interval.detected > xAxis.max) {
                    continue;
                  }

                  const detectX = xAxis.getPixelForValue(interval.detected);
                  const endX = xAxis.getPixelForValue(interval.end);
                  
                  const minX = Math.min(detectX, endX);
                  const maxX = Math.max(detectX, endX);
                  const isNarrow = (maxX - minX) < 8;

                  // Only show when cursor is in the area after leak detected and before leak cleared (if cleared).
                  // For a narrow vertical spike (< 8px), allow a subtle ±4px hit tolerance on the detection marker.
                  const triggerMinX = isNarrow ? minX - 4 : minX - 1;
                  const triggerMaxX = isNarrow ? maxX + 4 : maxX + 1;

                  if (xPos < triggerMinX || xPos > triggerMaxX) {
                    continue; // Skip: cursor is before leak detected or after leak cleared!
                  }

                  const dist = (xPos >= minX && xPos <= maxX) ? 0 : Math.min(Math.abs(xPos - minX), Math.abs(xPos - maxX));

                  if (dist < minDistancePx) {
                    minDistancePx = dist;
                    activeAnomaly = a;
                  }
                }
                
                if (activeAnomaly) {
                  const dTime = new Date(activeAnomaly.detected_at || activeAnomaly.last_seen || Date.now());
                  const interval = leakInterval(activeAnomaly)!;
                  const sTime = new Date(interval.start);
                  const flowAlarmRaw = activeAnomaly.flow_alarm;
                  const flowAlarmFlags = activeAnomaly.flow_alarm_flags;
                  
                  const leakType = activeAnomaly.log_type === 'hardware'
                    ? (flowAlarmRaw > 0 ? formatFlowAlarm(flowAlarmFlags, flowAlarmRaw) : 'Sensor Alert')
                    : (activeAnomaly.anomaly_type || 'ML Anomaly');
                    
                  const severity = activeAnomaly.severity || 'critical';
                  const status = activeAnomaly.status || 'active';
                  const isShutoff = activeAnomaly.auto_shutoff;
                  const avgFlow = activeAnomaly.avg_flow_rate_lph;
                  
                  const lines = [
                    '',
                    '─── LEAK DETAILS ───',
                    `Event: ${leakType}`,
                    `Status: ${status.toUpperCase()} ${status === 'active' ? '⚠️' : '✅'}`,
                    `Severity: ${severity.toUpperCase()}`,
                    `Flow Started: ${interval.hasStart ? sTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Unknown'}`,
                    `Leak Detected: ${dTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`,
                  ];
                  
                  if (activeAnomaly.cleared_at) {
                    const cTime = new Date(activeAnomaly.cleared_at);
                    lines.push(`Leak Cleared: ${cTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`);
                  }

                  // Look up telemetry sample closest to leak detection time
                  const leakTime = interval.detected;
                  const closestSample = series.find((p: any) => p.time >= leakTime) || series.filter((p: any) => p.time <= leakTime).at(-1);
                  
                  if (avgFlow != null && avgFlow > 0) {
                    const flowRateStr = convertFlowRate(avgFlow).toFixed(2);
                    const flowRateUnit = unitPreference === 'imperial' ? 'GPM' : 'L/h';
                    lines.push(`Avg Flow Rate: ${flowRateStr} ${flowRateUnit}`);
                  } else if (closestSample?.rate != null) {
                    lines.push(`Flow Rate: ${closestSample.rate.toFixed(2)} ${unitPreference === 'imperial' ? 'GPM' : 'L/h'}`);
                  }

                  const leakMeterTotal = activeAnomaly.total_flow_at_detection_gal ?? (closestSample?.total != null ? closestSample.total : null);
                  if (leakMeterTotal != null) {
                    lines.push(`Meter Total: ${leakMeterTotal.toFixed(2)} ${volumeUnit()}`);
                  }
                  
                  let durationSecs = activeAnomaly.leak_duration_s;
                  if (activeAnomaly.cleared_at && interval.hasStart) {
                    durationSecs = Math.round((new Date(activeAnomaly.cleared_at).getTime() - sTime.getTime()) / 1000);
                  }
                  if (durationSecs) {
                    const mins = Math.floor(durationSecs / 60);
                    const secs = durationSecs % 60;
                    lines.push(`Duration: ${mins}m ${secs}s`);
                  }
                  
                  lines.push(`Auto Shutoff: ${isShutoff ? 'TRIGGERED 🛑' : 'Disabled / Inactive'}`);
                  
                  return lines.join('\n');
                }
                return '';
              }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              modifierKey: null,
              onPan: () => { isZoomed = true; publishViewport(); },
            },
            limits: {
              x: { 
                min: timeWindow?.start ?? Date.now() - (range === 'Live' ? 86400_000 : range === '30d' ? 30 * 86400_000 : range === '7d' ? 7 * 86400_000 : 86400_000),
                max: timeWindow?.end ?? Date.now(),
                minRange: timeWindow ? Math.min(60_000, timeWindow.end - timeWindow.start) : range === 'Live' ? 600_000 : 60_000 // Prevent zooming in closer than 10m in Live, 1m in history
              }
            },
            zoom: {
              wheel: { enabled: true, speed: range === 'Live' ? 0.025 : 0.1 },
              pinch: { enabled: true },
              drag: {
                enabled: true,
                modifierKey: 'shift',
                threshold: 8,
                backgroundColor: color + '15',
                borderColor: color + '40',
                borderWidth: 1,
              },
              mode: 'x',
              onZoom: () => { isZoomed = true; publishViewport(); },
              onZoomComplete: () => { isZoomed = true; publishViewport(); },
              onPanComplete: () => { isZoomed = true; publishViewport(); },
            }
          },
          annotation: {
            annotations: {}
          }
        },
        scales: {
          x: {
            type: 'time',
            time: { displayFormats: { minute: 'HH:mm', hour: 'MMM d HH:mm', day: 'MMM d' } },
            ...(timeWindow ? { min: timeWindow.start, max: timeWindow.end } : range === 'Live' ? {
              time: {
                tooltipFormat: 'PPpp',
                displayFormats: {
                  millisecond: 'HH:mm:ss',
                  second: 'HH:mm:ss',
                  minute: 'HH:mm:ss',
                  hour: 'HH:mm',
                }
              },
              min: Date.now() - 600_000,
              max: Date.now(),
            } : { min: Date.now() - (range === '30d' ? 30 : range === '7d' ? 7 : 1) * 86400_000, max: Date.now() }),
            grid: { display: true },
            ticks: {
              font: { size: 10, family: 'Inter' },
              color: tc.tick,
              maxTicksLimit: range === 'Live' ? 6 : (range === '30d' ? 10 : 8),
              maxRotation: range === '7d' ? 45 : 0,
              align: 'inner', // Keep edge tick labels within the chart bounds so the last one is never cropped/hidden
              includeBounds: true, // Force the start and end boundary timestamps to be rendered as ticks
            },
            border: { display: false },
          },
          y: {
            position: 'left',
            afterFit: (axis: any) => { axis.width = 60; },
            grid: {
              color: tc.grid,
              lineWidth: 0.5,
            },
            ticks: {
              font: { size: 10, family: 'Inter' },
              color: tc.tick,
              maxTicksLimit: 5,
            },
            border: { display: false },
            beginAtZero: !metric,
            grace: '15%',
            title: {
              display: showTF,
              text: label,
              font: { size: 10, family: 'Inter' },
              color: color,
            },
          },
          ...(metric ? { y1: { position: 'right', min: 0, max: 1, afterFit: (axis: any) => { axis.width = 60; }, grid: { display: false }, ticks: { display: false }, border: { display: false } } } : {}),
          ...(sensorSeries.length ? Object.fromEntries(['y', 'y1'].map(axis => {
            const sensor = sensorSeries.find((item: any) => item.axis === axis);
            return [axis, {beginAtZero: axis === 'y', position:axis === 'y' ? 'left' : 'right', afterFit:(scale: any)=>{scale.width=60;},
              grid:{display:axis === 'y',color:tc.grid}, border:{display:false},
              grace: '10%',
              ticks:{display:!!sensor,color:sensor?.color,font:{size:10},maxTicksLimit:4},
              title:{display:!!sensor,text:sensor?.label,color:sensor?.color,font:{size:10}}}];
          })) : {}),
          ...(valveReports.length ? {valveAxis:{display:false,min:0,max:1}} : {}),
          ...(showTF ? {
            y1: {
              position: 'right' as const,
              afterFit: (axis: any) => { axis.width = 60; },
              grid: { display: false },
              ticks: {
                font: { size: 10, family: 'Inter' },
                color: totalFlowColor,
                maxTicksLimit: 5,
              },
              border: { display: false },
              beginAtZero: !customVolumeUnit,
              grace: '10%',
              title: {
                display: true,
                text: `${volumeLabel} (${volumeUnit()})`,
                font: { size: 10, family: 'Inter' },
                color: totalFlowColor,
              },
            }
          } : {}),
        },
      },
    });
  }

  function publishViewport() {
    if (chart) onViewport?.({ min: chart.scales.x.min, max: chart.scales.x.max, paused: isZoomed });
  }

  $effect(() => {
    const bounds = sharedViewport;
    if (bounds && chart) {
      isZoomed = bounds.paused;
      Object.assign(chart.options.scales!.x!, {min: bounds.min, max: bounds.max});
      chart.update('none');
    }
  });
  $effect(() => { const hover = sharedHover; chart?.draw(); });

  function zoomTime(factor: number) {
    if (!chart) return;
    const effectiveFactor = factor > 1
      ? (range === '30d' ? 2.5 : range === '7d' ? 1.8 : 1.3)
      : (range === '30d' ? 0.4 : range === '7d' ? 0.55 : 0.75);
    chart.zoom(effectiveFactor, 'none');
    chart.update('none');
    isZoomed = true;
    publishViewport();
  }

  function panTime(direction: 'earlier' | 'later') {
    if (!chart) return;
    const x = chart.scales.x;
    chart.pan({ x: (x.right - x.left) * 0.25 * (direction === 'earlier' ? 1 : -1) }, undefined, 'none');
    chart.update('none');
    isZoomed = true;
    publishViewport();
  }

  function resetZoom() {
    chart?.resetZoom('none');
    chart?.update('none');
    isZoomed = false;
    publishViewport();
  }

  // Observe dark mode class changes to rebuild chart with correct colors
  let observer: MutationObserver | null = null;

  $effect(() => {
    if (typeof MutationObserver !== 'undefined' && !observer) {
      observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.attributeName === 'class') {
            if (data && canvasEl) buildChart();
          }
        }
      });
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
  });

  // Rebuild from all measured samples once per update; never append a second copy
  // or integrate a guessed volume while waiting for the next radio report.
  let chartRange = '';
  $effect(() => {
    const tracked = [data, range, unitPreference, label, color, JSON.stringify(anomalies), JSON.stringify(timeWindow), JSON.stringify(sensorSeries), JSON.stringify(valveReports)];
    if (!canvasEl) return;
    untrack(() => {
      const rangeKey = range + JSON.stringify(timeWindow);
      if (chartRange !== rangeKey) isZoomed = false;
      chartRange = rangeKey;
      const bounds = sharedViewport ?? (isZoomed && chart ? { min: chart.scales.x.min, max: chart.scales.x.max } : null);
      buildChart();
      if (bounds && chart?.options.scales?.x) {
        Object.assign(chart.options.scales.x, {min: bounds.min, max: bounds.max});
        if (bounds.paused != null) isZoomed = bounds.paused;
      }
      chart?.update('none');
    });
  });

  $effect(() => {
    if (range !== 'Live') return;
    const timer = setInterval(() => {
      liveTimeStr = new Date().toLocaleTimeString();
      if (!isZoomed && chart?.options.scales?.x) {
        chart.options.scales.x.min = Date.now() - 600_000;
        chart.options.scales.x.max = Date.now();
        // Only the last-known tail moves with the clock. Completed historical
        // gaps must stop at the next report, or they overlap newer readings.
        for (const continuation of chart.data.datasets.filter((dataset: any) => dataset.liveContinuation)) (continuation.data[1] as any).x = Date.now();
        chart.update('none');
        if (controls) publishViewport();
      }
    }, 1000);
    return () => clearInterval(timer);
  });

  onDestroy(() => {
    chart?.destroy();
    observer?.disconnect();
  });

</script>

<div class="w-full h-full {metric ? 'min-h-[120px]' : 'min-h-[200px]'} relative group">
  <canvas bind:this={canvasEl} aria-label={metric ? label + " history" : "Reported flow rate, volume, and leak events"} onmouseleave={() => onHoverTime?.(null)}></canvas>
  {#if data.length === 0 || (metric && !(sensorSeries.length ? sensorSeries.some((sensor: any) => sensor.points.some((row: any) => row.value != null)) : data.some((row: any) => row.value != null)))}<div class="absolute inset-0 flex items-center justify-center text-sm opacity-60 pointer-events-none">{metric ? "No reported readings in this range" : "No reported flow measurements in this range"}</div>{/if}

  {#if lastKnown}
    <span class="absolute bottom-1 left-12 text-[10px] text-base-content/60 bg-base-100/80 px-1 rounded" title={`Last reported ${formatTooltipTitle(lastKnown.timestamp ?? lastKnown.ingested_at)}. Dashed continuation is unconfirmed and is not used for consumption or leak detection.`}>Dashed: last known total · {formatTooltipTitle(lastKnown.timestamp ?? lastKnown.ingested_at)}</span>
  {/if}

  {#if controls}
  <!-- Top-center controls -->
  <div class="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity">
    <button class="btn btn-xs btn-ghost bg-base-100/80" aria-label="Pan earlier" title="Pan earlier by a quarter window" onclick={() => panTime('earlier')}>←</button>
    <button class="btn btn-xs btn-ghost bg-base-100/80" aria-label="Pan later" title="Pan later by a quarter window" onclick={() => panTime('later')}>→</button>
    <button class="btn btn-xs btn-ghost bg-base-100/80" aria-label="Zoom in" title="Zoom in" onclick={() => zoomTime(1.2)}>+</button>
    <button class="btn btn-xs btn-ghost bg-base-100/80" aria-label="Zoom out" title="Zoom out" onclick={() => zoomTime(0.8)}>−</button>
    {#if isZoomed}
      <button class="btn btn-xs btn-outline bg-base-100 border-base-300 text-xs shadow-sm hover:bg-base-200 gap-1" onclick={resetZoom}>
        Reset Zoom
      </button>
    {/if}
  </div>

  {/if}

  {#if controls && range === 'Live' && liveTimeStr && !isZoomed}
    <!-- Restored live clock badge at original bottom-right position -->
    <div class="absolute bottom-1 right-2 z-10 flex items-center gap-2 text-[10px] sm:text-xs font-mono font-medium opacity-60 pointer-events-none">
      <span class="w-1.5 h-1.5 bg-error rounded-full animate-pulse"></span> {liveTimeStr}
    </div>
  {/if}

  <!-- Interactive controls instructions at bottom-right (away from top-left so it never blocks tooltips) -->
  <div class="hidden sm:block absolute bottom-1 right-2 z-0 text-[9px] text-base-content/40 bg-base-100/60 backdrop-blur-sm px-1.5 py-0.5 rounded border border-base-200/40 pointer-events-none opacity-0 group-hover:opacity-80 transition-opacity">
    Scroll to zoom · Drag to pan · Shift+drag to select
  </div>
</div>
